import { type ColorToken, DEFAULT_CONFIG, ImageCard, MeshGradient } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { AddIconChip } from '@backrs_ui/grow-ui/src/components/MyColleges/AddIconChip';
import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import { EstimatedAmount } from '@backrs_ui/grow-ui/src/pages/MyColleges/components/NetCostText';
import {
  COMPARE_FACTORS,
  ComparedCollege,
  ScoredComparison,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

import { BestForWatch, TopMatchBadge } from './CompareBadges';
import { factFor, factorLabel, fitFill, isEstimate, topMatchId } from '../compare.util';

const STRINGS = appStrings.modules.myColleges.compare;

type CompareCardsProps = Readonly<{ comparison: ScoredComparison }>;

export const CompareCards = ({ comparison }: CompareCardsProps) => {
  const topId = topMatchId(comparison.colleges);

  return (
    <Box id="compare-cards" sx={gridSx}>
      {comparison.colleges.map((college) => (
        <CompareCard
          key={college.institution_id}
          college={college}
          isTop={college.institution_id === topId}
        />
      ))}
    </Box>
  );
};

type CompareCardProps = Readonly<{ college: ComparedCollege; isTop: boolean }>;

const CompareCard = ({ college, isTop }: CompareCardProps) => (
  <ImageCard
    id={`compare-card-${college.institution_id}`}
    imageUrl={college.banner_media_url ?? ''}
    fallback={<MeshGradient config={DEFAULT_CONFIG} />}
    imageOverlay={
      <>
        {isTop && (
          <Box sx={topMatchSx}>
            <TopMatchBadge size="md" />
          </Box>
        )}
        <Box sx={chipAreaSx}>
          <CollegeChip id={`compare-card-college-${college.institution_id}`} college={college} />
          {college.ipeds_id !== null && (
            <AddIconChip
              id={`compare-card-add-${college.institution_id}`}
              college={{ ...college, ipeds_id: college.ipeds_id, school_name: college.name }}
            />
          )}
        </Box>
      </>
    }
    sxOverrides={{ container: cardSx, titleDescChildContainer: bodyContainerSx }}
  >
    <Box component="dl" sx={factsSx}>
      {COMPARE_FACTORS.map((factor) => {
        const { fit } = college.factors[factor];
        const fact = factFor(college, factor);

        return (
          <Box key={factor} sx={factRowSx}>
            <Typography component="dt" variant="text-sm-regular" sx={labelSx}>
              {factorLabel(factor)}
            </Typography>
            <Box component="dd" sx={valueSx}>
              {fact === null ? (
                <Typography variant="text-sm-regular" sx={mutedSx}>
                  {STRINGS.noData}
                </Typography>
              ) : (
                <Chip
                  variant="pill"
                  size="md"
                  label={isEstimate(college, factor) ? <EstimatedAmount amount={fact} /> : fact}
                  sx={factPillSx(fitFill(fit))}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>

    {(college.best_at || college.watch) && (
      <Box sx={notesSx}>
        <BestForWatch college={college} />
      </Box>
    )}
  </ImageCard>
);

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
  gap: backrs.spacing['spacing-xl'],
});

const cardSx: SxProps<Theme> = {
  width: 'auto',
  maxWidth: { xs: 'none', sm: 'none' },
  cursor: 'auto',
  userSelect: 'auto',
  '&:hover': { boxShadow: 'none', transform: 'none' },
};

const bodyContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: 0,
  padding: 0,
};

const topMatchSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
});

const chipAreaSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  left: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
  bottom: backrs.spacing['spacing-lg'],
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const factsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  margin: 0,
  padding: backrs.spacing['spacing-xl'],
});

const factRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const valueSx: SxProps<Theme> = {
  margin: 0,
};

const factPillSx =
  (fill: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    paddingBlock: backrs.spacing['spacing-xxs'],
    paddingInline: backrs.spacing['spacing-md'],
    borderColor: backrs.colors['Brand/200'],
    backgroundColor: backrs.colors[fill],
    color: backrs.colors['Brand/700'],
  });

const notesSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: 'auto',
  paddingInline: backrs.spacing['spacing-xl'],
  paddingBlock: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
